import { useEffect, useMemo, useState } from 'react';
import { StanDropdown, StanLoader, StanModal, StanProgressBar } from '@/components';
import { StanCheckbox } from '@/components/core-components/StanCheckbox/StanCheckbox';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { useBulkCustomerActions } from '../../hooks/useBulkCustomerActions';
import { type GrantableProductOption, useGrantableProducts } from '../../hooks/useGrantableProducts';
import { formatNumber } from '@/lib';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

interface BulkGrantProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalCustomers: number;
    filterArray: string | undefined;
}

type PromptStep = 'selection' | 'confirmation';

export function BulkGrantProductModal({ isOpen, onClose, totalCustomers, filterArray }: BulkGrantProductModalProps) {
    const [currentStep, setCurrentStep] = useState<PromptStep>('selection');
    const [selectedProduct, setSelectedProduct] = useState<GrantableProductOption | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { dropdownOptions, findProductByPageId } = useGrantableProducts();

    const { batchState, isLoading, grantProductAccess, resetState } = useBulkCustomerActions({
        filterArray,
        totalCustomers,
        onSuccess: () => {
            setCurrentStep('selection');
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            onClose();
        },
    });

    useEffect(() => {
        if (isOpen) {
            setCurrentStep('selection');
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            resetState();
        }
    }, [isOpen, resetState]);

    const handleClose = () => {
        if (!isLoading) {
            setCurrentStep('selection');
            setSelectedProduct(null);
            setConfirmed(false);
            setError(null);
            resetState();
            onClose();
        }
    };

    const handleProductSelect = (value: DropdownValue) => {
        const product = findProductByPageId(value.value);
        if (product) {
            setSelectedProduct(product);
            setConfirmed(true);
            setError(null);
        }
    };

    const handleContinue = () => {
        if (!selectedProduct) {
            setError('Please select a product!');
            return;
        }
        setError(null);
        setConfirmed(false);
        setCurrentStep('confirmation');
    };

    const handleSubmit = () => {
        if (!selectedProduct || !confirmed) return;
        grantProductAccess(selectedProduct.page_id);
    };

    const handleBack = () => {
        setCurrentStep('selection');
        setConfirmed(false);
    };

    const modalTitle = useMemo(() => {
        if (isLoading) return 'Processing...';
        if (currentStep === 'confirmation') return 'Confirming!';
        const customerText = totalCustomers === 1 ? 'customer' : 'customers';
        return `Grant Product Access to ${totalCustomers} ${customerText}`;
    }, [isLoading, currentStep, totalCustomers]);

    const customerText = totalCustomers === 1 ? 'customer' : 'customers';

    const currentValue = selectedProduct
        ? { label: selectedProduct.name, value: String(selectedProduct.page_id) }
        : null;

    return (
        <StanModal
            id='bulk-grant-product-modal'
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            primaryActionLabel={isLoading ? undefined : 'Grant Product Access'}
            secondaryActionLabel={isLoading ? undefined : currentStep === 'confirmation' ? 'Back' : 'Cancel'}
            primaryActionDisabled={currentStep === 'selection' ? !selectedProduct : !confirmed}
            primaryActionLoading={isLoading}
            onPrimaryAction={currentStep === 'selection' ? handleContinue : handleSubmit}
            onSecondaryAction={currentStep === 'confirmation' ? handleBack : handleClose}
            size='md'
        >
            <div className='flex flex-col gap-4'>
                {isLoading ? (
                    <div className='flex flex-col items-center gap-6 py-8'>
                        <StanLoader />
                        <StanProgressBar
                            min={0}
                            max={batchState.totalCount}
                            value={batchState.processedCount}
                            className='w-full'
                        />
                        <p className='text-gray text-label-xs-regular text-center'>
                            {batchState.processedCount} / {batchState.totalCount} customers processed
                        </p>
                    </div>
                ) : currentStep === 'selection' ? (
                    <div className='flex flex-col gap-4'>
                        <DropdownProvider
                            dropdownId='product-select'
                            dropdownName='product-select'
                            initialValue={currentValue}
                        >
                            <StanDropdown
                                id='product-select'
                                name='product-select'
                                options={dropdownOptions}
                                onSelect={handleProductSelect}
                                placeholder='Select a product'
                                currentValue={currentValue}
                            />
                        </DropdownProvider>
                        {error && <p className='text-negative text-label-xs-regular'>{error}</p>}
                    </div>
                ) : (
                    <div className='flex flex-col gap-6'>
                        <div className='bg-neutral-subtle flex flex-col items-center justify-center rounded-2xl py-10'>
                            <span className='text-dark text-6xl font-bold'>
                                {formatNumber(totalCustomers, { decimals: 0 })}
                            </span>
                            <span className='text-gray text-label-sm-regular mt-8'>Recipients</span>
                        </div>

                        <label className='flex cursor-pointer items-center justify-center gap-2 rounded-xl p-4'>
                            <StanCheckbox
                                checked={confirmed}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmed(e.target.checked)}
                            />
                            <span className='text-label-sm-regular text-dark'>
                                Are you sure you want to grant product access to{' '}
                                <span className='text-brand-main'>
                                    {totalCustomers} {customerText}
                                </span>
                                ?
                            </span>
                        </label>
                    </div>
                )}
            </div>
        </StanModal>
    );
}

BulkGrantProductModal.displayName = 'BulkGrantProductModal';
